import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';

import { getLowQualityPreviewUrl } from 'src/helpers/imageUrlUtils';

interface Props {
  src: string;
  alt?: string;
  className?: string;
  title?: string;
  sx?: SxProps<Theme>;
  objectFit?: CSSProperties['objectFit'];
  priority?: boolean;
  fill?: boolean;
  'data-testid'?: string;
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return prefersReducedMotion;
}

function isImageAlreadyLoaded(img: HTMLImageElement | null): boolean {
  return Boolean(img?.complete && img.naturalWidth > 0);
}

export default function ProgressiveImage({
  src,
  alt,
  className,
  title,
  sx,
  objectFit = 'cover',
  priority = false,
  fill = false,
  'data-testid': dataTestId,
}: Props) {
  const [loaded, setLoaded] = useState(false);
  const mainRef = useRef<HTMLImageElement | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const previewSrc = getLowQualityPreviewUrl(src);
  const showSharp = loaded || prefersReducedMotion;

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    if (isImageAlreadyLoaded(mainRef.current)) {
      setLoaded(true);
    }
  }, [src]);

  const loadingProps = priority
    ? ({ fetchPriority: 'high' as const, loading: 'eager' as const })
    : { loading: 'lazy' as const };

  const transition = prefersReducedMotion
    ? 'none'
    : 'opacity 0.4s ease, filter 0.4s ease';

  const containerSx: SxProps<Theme> = fill
    ? {
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
      }
    : {
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-block',
        maxWidth: '100%',
      };

  const imageBaseSx: SxProps<Theme> = {
    display: 'block',
    width: fill ? '100%' : '100%',
    height: fill ? '100%' : 'auto',
    maxWidth: '100%',
    objectFit,
  };

  const fillImageSx: SxProps<Theme> = {
    ...imageBaseSx,
    position: 'absolute',
    inset: 0,
  };

  const previewImageSx: SxProps<Theme> = fill
    ? {
        ...fillImageSx,
        filter: showSharp ? 'blur(0)' : 'blur(20px)',
        opacity: showSharp ? 0 : 1,
        transition,
      }
    : {
        ...imageBaseSx,
        position: 'relative',
        filter: showSharp ? 'blur(0)' : 'blur(20px)',
        opacity: showSharp ? 0 : 1,
        transition,
      };

  const mainImageSx: SxProps<Theme> = fill
    ? {
        ...fillImageSx,
        filter: showSharp ? 'blur(0)' : 'blur(20px)',
        opacity: showSharp ? 1 : 0,
        transition,
      }
    : {
        ...imageBaseSx,
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        filter: showSharp ? 'blur(0)' : 'blur(20px)',
        opacity: showSharp ? 1 : 0,
        transition,
      };

  const handleMainLoad = () => setLoaded(true);

  return (
    <Box
      className={className}
      title={title}
      data-testid={dataTestId}
      sx={[containerSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      <Box component="img" src={previewSrc} alt="" aria-hidden sx={previewImageSx} />
      <Box
        component="img"
        ref={mainRef}
        src={src}
        alt={alt ?? ''}
        onLoad={handleMainLoad}
        onError={handleMainLoad}
        {...loadingProps}
        sx={mainImageSx}
      />
    </Box>
  );
}
